/* ============================================================
   Reading - the KB article's scrollspy and progress bar

   Carried from the marketing theme's `components/reading.js`, minus the parts
   Hub has no counterpart for: the theme drives the document from Lenis's own rAF
   loop, so it subscribes through its scroller; Hub scrolls natively, so the
   browser's own `scroll` event is the signal.
   ============================================================ */

/* ── THE LIST IS ALREADY ON THE PAGE ────────────────────────────────────────
   It is printed by the server, from the same headings, with the ids already on
   them (`DocHeadingAnchors::process` + `<x-frontend.toc>`). Building it here
   would mean the one navigation aid a long document has does not exist until a
   script has run, and the anchors a reader shares point at ids no crawler ever
   saw.

   What is left is the part that genuinely needs a browser: pairing each heading
   with the row (or rows - the rail and the inline copy print the same list) that
   points at it. A heading with no row is skipped rather than tracked. */
function collectToc() {
    const content = document.querySelector('[data-toc-content]')
    const tocs = Array.from(document.querySelectorAll('[data-toc]'))
    if (!content || !tocs.length) return null

    const entries = []
    Array.from(content.querySelectorAll('h2[id], h3[id], h4[id]')).forEach((heading) => {
        const links = []
        tocs.forEach((toc) => {
            /* BY ATTRIBUTE, NOT BY `#id` SELECTOR. A heading id is whatever the
               document made it, and an attribute value takes it verbatim. */
            toc.querySelectorAll(`.toc__link[href="#${heading.id}"]`).forEach((a) => links.push(a))
        })
        if (links.length) entries.push({ heading, links })
    })

    return entries.length ? entries : null
}

/* Where in the viewport a heading counts as "the one you are reading" - the top
   third, which is where the eye sits on a long document. */
const ACTIVE_LINE = 0.3

/* ONE ROW IS ALWAYS MARKED, AND THAT IS THE WHOLE POINT OF THIS BEING A RESOLVER
   RATHER THAN AN OBSERVER. An IntersectionObserver that lights whichever heading
   is inside a band leaves two states with NOTHING marked: the top of the page,
   before the first heading reaches the band, and the foot of it, after the last
   one has left. Both are states a reader is actually in - the top is where every
   reader starts - and a contents rail with no current row says the document has
   no position rather than that it is at the beginning of one.

   So the question is not "which heading is visible" but "which heading have I got
   to", and that has an answer everywhere on the page. */
function initScrollspy(entries) {
    if (!entries || !entries.length) return

    let current = null

    const setActive = (id) => {
        if (id === current) return
        current = id
        entries.forEach((e) => {
            const on = e.heading.id === id
            e.links.forEach((l) => l.classList.toggle('is-active', on))
        })
    }

    const resolve = () => {
        const line = window.innerHeight * ACTIVE_LINE
        let found = entries[0]
        entries.forEach((e) => {
            if (e.heading.getBoundingClientRect().top <= line) found = e
        })
        setActive(found.heading.id)
    }

    resolve()
    window.addEventListener('scroll', resolve, { passive: true })
    window.addEventListener('resize', resolve, { passive: true })
}

/* The hairline across the top of the window. It measures the ARTICLE, not the
   document: a page whose footer is three screens tall would otherwise report a
   third of itself unread at the last paragraph. */
function initProgress() {
    const bar = document.getElementById('read-progress')
    const article = document.querySelector('.single__body')
    if (!bar || !article) return

    const update = () => {
        const rect = article.getBoundingClientRect()
        const total = rect.height - window.innerHeight
        const scrolled = -rect.top
        let ratio = total > 0 ? scrolled / total : (rect.top <= 0 ? 1 : 0)
        ratio = Math.min(1, Math.max(0, ratio))
        bar.style.width = `${Math.round(ratio * 100)}%`
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update, { passive: true })
}

/* The copy-link button in the share row.

   TWO WAYS OF COPYING, AND THE SECOND ONE IS NOT OPTIONAL. `navigator.clipboard`
   exists only in a SECURE CONTEXT - https, or localhost by name. A staging host
   over plain http (`hub.test`) has no `navigator.clipboard` at all, so the await
   threw, the catch swallowed it, and the button did nothing with nothing in the
   console to say why. That is most of the time somebody is looking at this
   during development.

   The fallback is a throwaway textarea and `execCommand('copy')` - deprecated,
   and the only thing that works there. It is off-screen rather than
   `display: none`, because a hidden element cannot hold a selection. */
async function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)

        return
    }

    const field = document.createElement('textarea')
    field.value = text
    field.setAttribute('readonly', '')
    field.style.cssText = 'position:fixed;top:-9999px;opacity:0'
    document.body.appendChild(field)
    field.select()
    document.execCommand('copy')
    field.remove()
}

function initCopyLink() {
    document.querySelectorAll('[data-copy-link]').forEach((btn) => {
        const glyph = btn.querySelector('.ti')
        let restore = null

        btn.addEventListener('click', async () => {
            try {
                await copyText(btn.dataset.copyUrl || window.location.href)
            } catch {
                // Nothing left to try - the share links beside it still work,
                // and a button that reports success it did not have is worse
                // than one that reports nothing.
                return
            }

            /* ── THE GLYPH IS WHAT SAYS IT WORKED (pass 398.40) ─────────────
               `is-copied` was added and the icon never changed: the class only
               moves a colour, so the whole confirmation was a link mark going
               slightly brighter for a moment. The tick is the acknowledgement,
               and swapping it is one line.

               AND THE TIMER IS RESET, NOT STACKED. Pressing twice queued two
               timeouts, and the FIRST one fired 1.6s after the first press -
               so a second press appeared to do nothing and then cleared
               itself early. That is the "unstable" part: it was two timers
               racing, not the copy failing. */
            if (restore) clearTimeout(restore)

            btn.classList.add('is-copied')
            if (glyph) glyph.classList.replace('ti-link', 'ti-check')

            // 2.4s. At 1.6 the tick was gone before a reader who had glanced
            // away looked back, which reads as the press not registering.
            restore = setTimeout(() => {
                btn.classList.remove('is-copied')
                if (glyph) glyph.classList.replace('ti-check', 'ti-link')
                restore = null
            }, 2400)
        })
    })
}

export default function initReading() {
    initScrollspy(collectToc())
    initProgress()
    initCopyLink()
}
